import { Outlet, useNavigate, useOutletContext, useParams } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { SectionHeader } from '@factorialco/f0-react/dist/experimental'
import { Delete, Link, Upload } from '@factorialco/f0-react/icons/app'
import {
  TrainingClassMaterialsDeleteTrainingClassFileDocument,
  TrainingClassMaterialsDeleteTrainingClassLinkDocument,
  TrainingClassMaterialsDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import {
  MaterialItem,
  MaterialsList,
  getSortableTitle,
} from 'modules/trainings/components/MaterialsList'
import { DetailOutletContext } from 'modules/trainings/pages/Trainings/Revamp/TrainingDetail/Detail/types'

export default function TrainingClassMaterials() {
  const { classId, id } = useParams()
  const { canEdit } = useOutletContext<DetailOutletContext>()
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const { company } = useCurrentSession()
  const [deleteTrainingClassFile] = useMutation(
    TrainingClassMaterialsDeleteTrainingClassFileDocument,
    {
      extractorFn: (data) => data.trainingsMutations.deleteTrainingsTrainingClassFile,
      awaitRefetchQueries: true,
      refetchQueries: [TrainingClassMaterialsDocument],
    }
  )

  const [deleteTrainingClassLink] = useMutation(
    TrainingClassMaterialsDeleteTrainingClassLinkDocument,
    {
      extractorFn: (data) => data.trainingsMutations.deleteTrainingsTrainingClassLink,
      awaitRefetchQueries: true,
      refetchQueries: [TrainingClassMaterialsDocument],
    }
  )

  const source = useGraphqlDataCollectionSource({
    // TODO: We're fetching trough the training class because there's an id mismatch
    // between the training class and the training class file at the entity level
    document: TrainingClassMaterialsDocument,
    search: {
      enabled: true,
    },
    mapVariables: ({ search }) => ({
      id: String(classId),
      companyId: company.id,
      search: search,
    }),
    itemUrl: (item: MaterialItem) => {
      switch (item.type) {
        case 'link': {
          return item.url
        }
        default: {
          return navigation.trainings.list.details_revamp.view.trainingClasses.detail.materials.previewFile.fullPath(
            {
              id: String(id),
              classId: String(classId),
              classFileId: String(item.id),
            }
          )
        }
      }
    },
    mapData: (data): MaterialItem[] => {
      const files =
        data?.trainings.trainingClass?.classFilesConnection.nodes.map((file) => ({
          contentType: file.contentType,
          filename: file.filename,
          id: file.id,
          size: file.size,
          title: file.title ?? undefined,
          type: 'trainingClass' as const,
        })) || []

      const links =
        data?.trainings.trainingClass?.classLinksConnection.nodes.map((link) => ({
          title: link.title,
          id: link.id,
          url: link.url,
          type: 'link' as const,
        })) || []

      return [...files, ...links].sort((a, b) =>
        getSortableTitle(a) > getSortableTitle(b) ? 1 : -1
      )
    },
    mapPagination: (data) => {
      const filesCount = data.trainings.trainingClass?.classFilesConnection.totalCount || 0
      const linksCount = data.trainings.trainingClass?.classLinksConnection.totalCount || 0
      return {
        total: filesCount + linksCount,
      }
    },
    itemActions: (item) => {
      if (!canEdit) return []
      return [
        {
          label: i18n.t('common.delete'),
          onClick: () => {
            switch (item.type) {
              case 'link': {
                deleteTrainingClassLink({ variables: { id: String(item.id) } })
                break
              }
              default: {
                deleteTrainingClassFile({ variables: { id: String(item.id) } })
              }
            }
          },
          icon: Delete,
        },
      ]
    },
    primaryActions: () => {
      if (!canEdit) return
      return {
        icon: Upload,
        label: i18n.t('common.upload'),
        onClick: () => {
          navigate(
            navigation.trainings.list.details_revamp.view.trainingClasses.detail.materials.newFile.fullPath(
              {
                id: String(id),
                classId: String(classId),
              }
            )
          )
        },
      }
    },
    secondaryActions: {
      expanded: 1,
      actions: () => [
        {
          label: i18n.t('trainings.trainings_revamp.classes.materials.new_link'),
          icon: Link,
          onClick: () => {
            navigate(
              navigation.trainings.list.details_revamp.view.trainingClasses.detail.materials.newLink.fullPath(
                {
                  id: String(id),
                  classId: String(classId),
                }
              )
            )
          },
        },
      ],
    },
  })

  return (
    <Box gap="s16">
      <SectionHeader
        title={i18n.t('trainings.trainings_revamp.training_class.materials.section_header.title')}
        description={i18n.t(
          'trainings.trainings_revamp.training_class.materials.section_header.description'
        )}
        separator="bottom"
      />
      <MaterialsList
        source={source}
        emptyStates={{
          'no-data': {
            title: i18n.t(
              'trainings.trainings_revamp.training_class.materials.datacollection.empty_state.no_data.title'
            ),
            description: i18n.t(
              'trainings.trainings_revamp.training_class.materials.datacollection.empty_state.no_data.description'
            ),
          },
        }}
      />
      <Outlet />
    </Box>
  )
}
